import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import {
  clearSelectedAccountId,
  getSelectedAccountId,
  normalizeSelectedAccountId,
  setSelectedAccountId,
} from '@shared/account/selection';
import type { ManagedAwsAccount, ManagedAwsAccountsResponse } from '@shared/types/api';

interface ClientAccountContextValue {
  accounts: ManagedAwsAccount[];
  selectedAccount: ManagedAwsAccount | null;
  loading: boolean;
  error: string | null;
  refreshAccounts: () => Promise<void>;
  selectAccount: (accountId: string) => void;
  accountPath: (path?: string) => string;
}

const ClientAccountContext = createContext<ClientAccountContextValue | null>(null);
const ACCOUNT_ROUTE_PATTERN = /^\/accounts\/(\d{12})(?:\/|$)/;

function accountIdFromPath(pathname: string): string | null {
  return normalizeSelectedAccountId(ACCOUNT_ROUTE_PATTERN.exec(pathname)?.[1]);
}

function accountDashboardPath(accountId: string): string {
  return `/accounts/${encodeURIComponent(accountId)}/dashboard`;
}

export function ClientAccountProvider({ children }: PropsWithChildren) {
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<ManagedAwsAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<ManagedAwsAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<ManagedAwsAccountsResponse>('/client/accounts');
      const activeAccounts = (response.accounts ?? []).filter(
        (account) => String(account.accountStatus || '').toUpperCase() === 'ACTIVE',
      );
      setAccounts(activeAccounts);

      const routeAccountId = accountIdFromPath(window.location.pathname);
      const storedAccountId = getSelectedAccountId();
      const requestedAccountId = routeAccountId || storedAccountId;
      const matchedAccount = requestedAccountId
        ? activeAccounts.find((account) => account.accountId === requestedAccountId) ?? null
        : null;

      if (routeAccountId && !matchedAccount) {
        clearSelectedAccountId();
        setSelectedAccount(null);
        navigate('/select-account', { replace: true });
        return;
      }

      if (matchedAccount) {
        setSelectedAccountId(matchedAccount.accountId);
        setSelectedAccount(matchedAccount);
        if (!routeAccountId) {
          navigate(accountDashboardPath(matchedAccount.accountId), { replace: true });
        }
        return;
      }

      if (activeAccounts.length === 1) {
        const onlyAccount = activeAccounts[0];
        setSelectedAccountId(onlyAccount.accountId);
        setSelectedAccount(onlyAccount);
        navigate(accountDashboardPath(onlyAccount.accountId), { replace: true });
        return;
      }

      clearSelectedAccountId();
      setSelectedAccount(null);
      if (window.location.pathname !== '/select-account') {
        navigate('/select-account', { replace: true });
      }
    } catch (requestError) {
      clearSelectedAccountId();
      setSelectedAccount(null);
      setError(requestError instanceof Error ? requestError.message : 'Unable to load authorized AWS accounts.');
    } finally {
      setLoading(false);
    }
  }, [api, navigate]);

  useEffect(() => {
    void refreshAccounts();
  }, [refreshAccounts]);

  useEffect(() => {
    if (loading) return;
    const routeAccountId = accountIdFromPath(location.pathname);
    if (!routeAccountId) return;
    const account = accounts.find((item) => item.accountId === routeAccountId) ?? null;
    if (!account) {
      clearSelectedAccountId();
      setSelectedAccount(null);
      navigate('/select-account', { replace: true });
      return;
    }
    setSelectedAccountId(account.accountId);
    setSelectedAccount(account);
  }, [accounts, loading, location.pathname, navigate]);

  const selectAccount = useCallback((accountId: string) => {
    const normalizedAccountId = normalizeSelectedAccountId(accountId);
    const account = normalizedAccountId
      ? accounts.find((item) => item.accountId === normalizedAccountId)
      : undefined;
    if (!account) {
      throw new Error('The selected AWS account is not assigned to this user.');
    }
    setSelectedAccountId(account.accountId);
    // A replacement navigation guarantees account-specific React state, AI sessions,
    // pending requests, page caches, and browser back navigation cannot restore the prior account.
    window.location.replace(accountDashboardPath(account.accountId));
  }, [accounts]);

  const accountPath = useCallback((path = '') => {
    if (!selectedAccount) return '/select-account';
    const normalizedPath = path ? `/${path.replace(/^\/+/, '')}` : '';
    return `/accounts/${encodeURIComponent(selectedAccount.accountId)}${normalizedPath}`;
  }, [selectedAccount]);

  const value = useMemo<ClientAccountContextValue>(() => ({
    accounts,
    selectedAccount,
    loading,
    error,
    refreshAccounts,
    selectAccount,
    accountPath,
  }), [accountPath, accounts, error, loading, refreshAccounts, selectAccount, selectedAccount]);

  return <ClientAccountContext.Provider value={value}>{children}</ClientAccountContext.Provider>;
}

export function useClientAccount(): ClientAccountContextValue {
  const value = useContext(ClientAccountContext);
  if (!value) {
    throw new Error('useClientAccount must be used within ClientAccountProvider.');
  }
  return value;
}
